import React, { useEffect, memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, CardBody, Button, Spinner, CardHeader } from 'reactstrap';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import {
  useCreateMutation,
  useUpdatePatchMutation,
  useDetailQuery,
  useListQuery,
  usePaginatedListQuery,
} from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { handleFormErrors } from 'utils/formErrorHandler';
import FormField from 'components/Form/Inputs/FormField';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useDebounce } from 'hooks/useDebounce';

// Define the form values type based on Inventory entity
interface CreateInventoryFormValues {
  shopId: string | undefined;
  shopItemId: string;
  shopItemName?: string;
  location: string;
  shelfLevel: number;
  quantity: number;
  sellPrice: number;
  nearestExpiryDate?: string;
}

// Strongly typed fields
const formFields: {
  id: keyof CreateInventoryFormValues;
  label: string;
  required?: boolean;
  disabled?: boolean;
  type?: string;
  placeholder?: string;
  defaultValue?: any;
}[] = [
  {
    id: 'shopItemId',
    label: 'Medicine Item',
    required: true,
    type: 'search-select',
    defaultValue: { value: '', label: '' },
  },
  { id: 'location', label: 'Location', required: true, placeholder: 'e.g., Aisle 3, Shelf 2' },
  { id: 'shelfLevel', label: 'Shelf Level', required: true, type: 'number' },
  { id: 'quantity', label: 'Quantity', required: true, type: 'number' },
  { id: 'sellPrice', label: 'Sell Price', required: true, type: 'number' },
  { id: 'nearestExpiryDate', label: 'Nearest Expiry Date', type: 'date' },
];

// Yup validation schema
const validationSchema = Yup.object({
  shopItemId: Yup.string().trim().required('Medicine Item is required'),
  location: Yup.string()
    .trim()
    .required('Location is required')
    .max(100, 'Location must be at most 100 characters'),
  shelfLevel: Yup.number()
    .required('Shelf Level is required')
    .integer('Shelf Level must be an integer')
    .min(0, 'Shelf Level must be at least 0'),
  quantity: Yup.number()
    .required('Quantity is required')
    .integer('Quantity must be an integer')
    .min(0, 'Quantity must be at least 0'),
  sellPrice: Yup.number()
    .required('Sell Price is required')
    .min(0, 'Sell Price must be at least 0')
    .test('decimal', 'Sell Price must have at most 2 decimal places', (value) => {
      if (value === undefined || value === null) return true;
      return /^\d+(\.\d{1,2})?$/.test(value.toString());
    }),
  nearestExpiryDate: Yup.date().nullable().min(new Date(), 'Expiry date cannot be in the past'),
});

// Pass id as props (composite key: shopId + shopItemId)
const AddUpdateInventoryForm: React.FC<{ itemId?: string; shopId?: string }> = ({
  itemId,
  shopId,
}) => {
  const isEditMode = Boolean(itemId) && Boolean(shopId);

  const navigate = useNavigate();

  // State for medicine search
  const [searchTerm, setSearchTerm] = useState<string>('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  // Fetch existing inventory data for edit mode
  const { data: fetchedInventory, isLoading: loadingInventory } =
    useDetailQuery<CreateInventoryFormValues>(API_ENDPOINTS.TENANT.INVENTORIES.GET_BY_SHOP_ITEM, {
      shopId: shopId ?? '',
      itemId: itemId ?? '',
    });

  //in case of edit mode, we will not allow changing the shop item, so, we will disable the shop item select field and show the current selected item as the only option. So, we need to fetch the shop item details(as already have)
  //fetchedInventory, so we will find and mark the formField with id shopItemId as disabled and set the options to the current selected item only

  formFields.forEach((field) => {
    if (field.id === 'shopItemId' && isEditMode && fetchedInventory) {
      field.disabled = isEditMode;
      field.defaultValue = {
        value: fetchedInventory?.shopItemId || '',
        label: fetchedInventory?.shopItemName || '',
      };
    }
  });

  // Fetch paginated search results with debouncing
  const { data: searchResults, isLoading: searchingItems } = usePaginatedListQuery(
    API_ENDPOINTS.TENANT.SHOP_ITEMS.BASE,
    {
      pageNumber: 1,
      pageSize: 50,
      search: debouncedSearchTerm,
      shopId: shopId,
    },
    {
      enabled: debouncedSearchTerm.length > 0, // Only search when there's input
    },
  );

  const { mutate: createInventory, isPending: creating } = useCreateMutation(
    API_ENDPOINTS.TENANT.INVENTORIES.BASE,
  );
  const { mutate: updateInventory, isPending: updating } = useUpdatePatchMutation(
    API_ENDPOINTS.TENANT.INVENTORIES.PATCH,
  );

  // Transform shop items data for select options
  // Use search results if searching, otherwise use initial shop items
  const shopItemOptions = searchResults?.items?.map((item: any) => ({
    value: item.id,
    label: `${item.name} (${item.sku})`,
  }));

  // Initial values - empty or from fetched data
  const initialValues: CreateInventoryFormValues = {
    shopId: shopId,
    shopItemId: fetchedInventory?.shopItemId ?? '',
    location: fetchedInventory?.location ?? '',
    shelfLevel: fetchedInventory?.shelfLevel ?? 0,
    quantity: fetchedInventory?.quantity ?? 0,
    sellPrice: fetchedInventory?.sellPrice ?? 0,
    nearestExpiryDate: fetchedInventory?.nearestExpiryDate?.slice(0, 10) ?? undefined,
  };

  const formik = useFormik<CreateInventoryFormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, formikHelpers: FormikHelpers<CreateInventoryFormValues>) => {
      const payload = {
        ...values,
        shopId,
        location: values.location.trim(),
        nearestExpiryDate: values.nearestExpiryDate || undefined,
      };

      const onSuccess = () => {
        navigate(SHOP_PATHS.INVENTORIES.replace(':shopId', shopId || ''));
      };

      const onError = (error: any) => {
        handleFormErrors<CreateInventoryFormValues>(error, formikHelpers);
      };

      if (isEditMode) {
        updateInventory(
          { data: payload, params: { shopId, shopItemId: itemId } },
          { onSuccess, onError },
        );
      } else {
        createInventory(payload, { onSuccess, onError });
      }
    },
  });

  // Handle select change for shop item
  const handleShopItemChange = (option: { value: string; label: string } | null) => {
    formik.setFieldValue('shopItemId', option ? option.value : '');
  };

  // Handle search input change for medicine
  const handleMedicineSearchChange = (searchInput: string) => {
    setSearchTerm(searchInput);
  };

  if (isEditMode && loadingInventory) {
    return (
      <Container className="py-5 text-center">
        <Spinner color="primary" />
      </Container>
    );
  }

  return (
    <Row className="justify-content-center">
      <Col md={12} lg={8}>
        <Card className="p-4 shadow-sm">
          <CardHeader>
            <h2 className="text-center fw-bold fs-2">
              {isEditMode ? 'Update Inventory' : 'Add New Inventory'}
            </h2>
          </CardHeader>

          <CardBody>
            <form onSubmit={formik.handleSubmit} noValidate>
              <Row>
                {formFields.map((field) => (
                  <Col key={field.id} sm={12} md={6} lg={6}>
                    <FormField
                      key={formik.values.shopItemId || 'no-selection'}
                      id={field.id}
                      label={field.label}
                      type={field.type}
                      required={field.required}
                      defaultValue={isEditMode ? field.defaultValue : undefined}
                      disabled={field.disabled || (isEditMode && field.id === 'shopItemId')}
                      value={formik.values[field.id]}
                      touched={formik.touched[field.id]}
                      error={formik.errors[field.id]}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      options={shopItemOptions}
                      onSelectChange={handleShopItemChange}
                      onSearchInput={handleMedicineSearchChange}
                      isSearching={searchingItems}
                      placeholder={field.placeholder}
                    />
                  </Col>
                ))}
              </Row>

              <div className="d-flex justify-content-center mt-4">
                <Button color="primary" type="submit" disabled={creating || updating}>
                  {creating || updating
                    ? isEditMode
                      ? 'Updating...'
                      : 'Creating...'
                    : isEditMode
                    ? 'Update Inventory'
                    : 'Add Inventory'}
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default memo(AddUpdateInventoryForm);
